import { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { FileUpload } from "primereact/fileupload";
import type { FileUploadHandlerEvent } from "primereact/fileupload";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { ProgressBar } from "primereact/progressbar";
import { Tag } from "primereact/tag";
import type { ToastMessage } from "primereact/toast";
import { useFlags } from "launchdarkly-react-client-sdk";
import { getDirectoryClient } from "sj-ui-toolkit/src/client";
import { themeClasses } from "sj-ui-toolkit/src/atlas/utils/themeClasses";

import { importUsersFromCSVAsync } from "@/api/users";
import { validateUtf8Encoding } from "@/helpers/files";

const CSV_TEMPLATE =
  "https://6720694.fs1.hubspotusercontent-na1.net/hubfs/6720694/import_users_template.csv";

// ---------------------------------------------------------------------------
// Sub-components — dialogs
// ---------------------------------------------------------------------------

interface InputRow {
  email: string;
  firstName: string;
  lastName: string;
  role: "learner" | "admin";
}

export interface AddUsersDialogProps {
  visible: boolean;
  onHide: () => void;
  currentTenantName: string;
  onSuccess: () => void;
  showToast: (opts: ToastMessage) => void;
}

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const EMPTY_INPUT_ROW: InputRow = { email: "", firstName: "", lastName: "", role: "learner" };

export function AddUsersDialog({
  visible,
  onHide,
  currentTenantName,
  onSuccess,
  showToast,
}: AddUsersDialogProps) {
  const { csvUploadService: isCsvUploadServiceEnabled } = useFlags();
  const [csvFiles, setCsvFiles] = useState<File[]>([]);
  const [totalSize, setTotalSize] = useState(0);
  const [inputValues, setInputValues] = useState<InputRow[]>([{ ...EMPTY_INPUT_ROW }]);
  const [validInputs, setValidInputs] = useState(false);
  const [inputErrorIndex, setInputErrorIndex] = useState<number[]>([]);
  const [inputSuccessIndex, setInputSuccessIndex] = useState<number[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [csvErrorMessages, setCsvErrorMessages] = useState<string[]>([]);
  const [emailErrors, setEmailErrors] = useState<(string | null)[]>([null]);
  const fileUploadRef = useRef<FileUpload>(null);

  const svcDirectoryClient = getDirectoryClient();

  useEffect(() => {
    const newEmailErrors = inputValues.map(user => {
      if (!user.email) return null;
      if (!user.email.includes("@")) return "Missing @ symbol.";
      const [localPart, domainPart] = user.email.split("@");
      if (!localPart) return "Missing local part before @.";
      if (!domainPart) return "Missing domain after @.";
      if (!domainPart.includes(".")) return "Domain missing period.";
      const tld = domainPart.split(".").pop() ?? "";
      if (tld.length < 2) return "TLD too short (min 2 chars).";
      if (!EMAIL_REGEX.test(user.email)) return "Invalid characters/format.";
      return null;
    });
    setEmailErrors(newEmailErrors);
    const filledInputs = inputValues.filter(u => u.email.length > 0);
    setValidInputs(
      filledInputs.length > 0 &&
        filledInputs.every(
          ({ email, firstName, lastName }) =>
            EMAIL_REGEX.test(email) && firstName.length > 0 && lastName.length > 0
        )
    );
  }, [inputValues]);

  const clearInputs = () => {
    setCsvFiles([]);
    setTotalSize(0);
    setInputValues([{ ...EMPTY_INPUT_ROW }]);
    setInputErrorIndex([]);
    setInputSuccessIndex([]);
    setCsvErrorMessages([]);
    setEmailErrors([null]);
  };

  const handleHide = () => {
    clearInputs();
    onHide();
  };

  const onTemplateSelect = async (e: { files: File[] }) => {
    setCsvErrorMessages([]);
    const file = e.files[0];
    if (!file) return;
    const validation = await validateUtf8Encoding(file);
    if (!validation.isValid) {
      showToast({
        severity: "error",
        summary: "Invalid File Encoding",
        detail: validation.error ?? "File encoding error",
        life: 5000,
      });
      setCsvErrorMessages(["File must be UTF-8 encoded"]);
      fileUploadRef.current?.clear();
      return;
    }
    setCsvFiles(e.files);
    setTotalSize(file.size ?? 0);
  };

  const onTemplateRemove = (file: File, callback: () => void) => {
    setCsvErrorMessages([]);
    setTotalSize(totalSize - file.size);
    setCsvFiles([]);
    callback();
  };

  const onTemplateClear = () => {
    setCsvErrorMessages([]);
    setTotalSize(0);
    setInputValues([{ ...EMPTY_INPUT_ROW }]);
    setInputErrorIndex([]);
    setCsvFiles([]);
    setEmailErrors([null]);
  };

  const handleUpload = async (_e: FileUploadHandlerEvent) => {
    setCsvErrorMessages([]);
    setIsSubmitting(true);
    setInputErrorIndex([]);
    const successfulIndices: number[] = [];
    const failedIndices: number[] = [];
    let extractedErrors: string[] = [];

    try {
      // Manual entry
      const filledInputs = inputValues
        .map((u, i) => ({ ...u, originalIndex: i }))
        .filter((u, i) => u.email.length > 0 || i === 0);

      if (validInputs) {
        if (emailErrors.some(e => e !== null)) {
          showToast({ severity: "error", detail: "Please fix the errors before submitting." });
          setIsSubmitting(false);
          return;
        }

        await Promise.all(
          filledInputs.map(async user => {
            const userToCreate = {
              email: user.email,
              name: { familyName: user.lastName, givenName: user.firstName },
              tenants: [
                {
                  tenant: currentTenantName,
                  ...(user.role === "admin" ? { setRoles: { grantRoles: ["tenant_admin"] } } : {}),
                },
              ],
              attributes: {},
              developerProfile: {},
            };
            try {
              const res = await svcDirectoryClient.createOrUpdateUser(userToCreate);
              if (!res.error) {
                showToast({ severity: "success", detail: `User created/updated: ${user.email}` });
                setInputSuccessIndex(prev => [...prev, user.originalIndex]);
                successfulIndices.push(user.originalIndex);
              }
            } catch {
              showToast({ severity: "error", detail: `Upload failed for user ${user.email}` });
              failedIndices.push(user.originalIndex);
              setInputErrorIndex(prev => [...prev, user.originalIndex]);
            }
          })
        );

        if (successfulIndices.length > 0) {
          onSuccess();
          setTimeout(() => {
            setInputValues(prev => {
              const remaining = prev.filter((_, i) => !successfulIndices.includes(i));
              return remaining.length > 0 ? remaining : [{ ...EMPTY_INPUT_ROW }];
            });
            setInputSuccessIndex([]);
          }, 2000);
        }
      }

      // CSV upload
      if (csvFiles.length > 0) {
        let res;
        if (isCsvUploadServiceEnabled) {
          res = await svcDirectoryClient.uploadCsvToDirectory(currentTenantName, csvFiles[0]!);
        } else {
          res = await importUsersFromCSVAsync(csvFiles[0]!);
        }

        if (res?.error) {
          const errorMessage =
            typeof res.error === "string"
              ? res.error
              : ((res.error as { message?: string })?.message ?? "CSV Upload Failed");
          showToast({ severity: "error", detail: errorMessage, life: 5000 });
          setCsvErrorMessages([errorMessage]);
          setIsSubmitting(false);
          return;
        }

        const rejectionDetails = (res as { data?: { rejection_details?: Record<string, unknown> } })
          ?.data?.rejection_details;
        if (!rejectionDetails || Object.keys(rejectionDetails).length === 0) {
          showToast({ severity: "success", detail: "File Uploaded Successfully" });
          onSuccess();
          setCsvErrorMessages([]);
          setTotalSize(0);
          setCsvFiles([]);
        } else {
          extractedErrors = Object.entries(rejectionDetails).map(([errorType, lineNumbers]) => {
            const readable = errorType.replace(/_/g, " ").replace(/^./, s => s.toUpperCase());
            const lines = Array.isArray(lineNumbers) ? lineNumbers.join(", ") : "";
            const label = Array.isArray(lineNumbers) && lineNumbers.length > 1 ? "lines" : "line";
            return `${readable} on ${label}: ${lines}`;
          });
          showToast({ severity: "error", detail: "CSV Upload Failed" });
          setCsvErrorMessages(extractedErrors);
        }
      }

      const safeToCloseManual =
        !filledInputs.filter(u => u.email).length ||
        (failedIndices.length === 0 && emailErrors.every(e => e === null));
      const safeToCloseCsv = !csvFiles.length || extractedErrors.length === 0;

      if (safeToCloseManual && safeToCloseCsv) {
        clearInputs();
        onHide();
      }
    } catch {
      showToast({ severity: "error", detail: "Error creating users" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const fileUploadHeaderTemplate = (options: {
    className: string;
    chooseButton: React.ReactNode;
  }) => {
    const formattedValue = fileUploadRef.current?.formatSize(totalSize) ?? "0 B";
    const progressValue = totalSize / 10000;
    return (
      <div
        className={options.className}
        style={{ backgroundColor: "transparent", display: "flex", alignItems: "center" }}
      >
        {options.chooseButton}
        <small className="ml-2">
          Download a starter{" "}
          <a
            href={CSV_TEMPLATE}
            target="_blank"
            rel="noreferrer"
            className={themeClasses.linkPrimary}
          >
            template
          </a>
          .
        </small>
        <div className="flex items-center gap-3 ml-auto">
          <span>{formattedValue} / 1 MB</span>
          <ProgressBar
            value={progressValue}
            showValue={false}
            className="mt-1.5"
            style={{ width: "10rem", height: "12px" }}
          />
        </div>
      </div>
    );
  };

  const fileUploadItemTemplate = (
    file: object,
    props: { formatSize: string; onRemove: (e: React.SyntheticEvent) => void }
  ) => {
    const f = file as File;
    return (
      <div>
        <div className="flex justify-center items-center flex-wrap max-w-xl gap-4 mx-auto mt-6">
          <strong className="my-auto">{f.name}</strong>
          <Tag value={props.formatSize} severity="success" className="mr-4" />
          <Button
            type="button"
            icon="pi pi-times"
            severity="danger"
            outlined
            rounded
            onClick={e => onTemplateRemove(f, () => props.onRemove(e))}
          />
        </div>
        <div className="mt-6 text-center">
          {csvErrorMessages.map((msg, i) => (
            <p className={`${themeClasses.textError} body-xs`} key={i}>
              {typeof msg === "string" ? msg : (msg as { message: string }).message}
            </p>
          ))}
        </div>
      </div>
    );
  };

  const fileUploadEmptyTemplate = () => (
    <div className="flex items-center justify-center flex-col">
      <i
        className={`pi pi-cloud-upload mt-3 p-5 rounded-full ${themeClasses.textPrimary} ${themeClasses.bgElevation2}`}
        style={{ fontSize: "5em" }}
      />
    </div>
  );

  return (
    <Dialog
      header="Add Users"
      data-testid="add-users-popup"
      visible={visible}
      onHide={handleHide}
      style={{ width: "80rem" }}
      blockScroll
      maskStyle={{ backdropFilter: "blur(5px)" }}
      footer={
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            icon="pi pi-times"
            label="Cancel"
            data-testid="cancel-add-users-button"
            className="p-button-outlined p-button-danger"
            onClick={handleHide}
          />
          <Button
            type="button"
            data-testid="upload-users-button"
            label="Add Users"
            icon="pi pi-user-plus"
            disabled={!csvFiles.length && !validInputs}
            onClick={() => void handleUpload({ files: csvFiles } as FileUploadHandlerEvent)}
            loading={isSubmitting}
          />
        </div>
      }
    >
      <div className="mt-4 mb-8">
        <FileUpload
          ref={fileUploadRef}
          name="demo[]"
          customUpload
          uploadHandler={handleUpload}
          accept=".csv"
          maxFileSize={1000000}
          onSelect={e => void onTemplateSelect(e)}
          onError={onTemplateClear}
          onClear={onTemplateClear}
          headerTemplate={fileUploadHeaderTemplate}
          itemTemplate={fileUploadItemTemplate}
          emptyTemplate={fileUploadEmptyTemplate}
          chooseOptions={{
            icon: "pi pi-cloud-upload",
            label: "Upload CSV",
            className: "p-button-primary p-button-outlined",
          }}
          progressBarTemplate={<></>}
        />
      </div>

      <div className="mb-3">
        {inputValues.map((input, index) => (
          <div
            key={index}
            className="mb-8 flex flex-row gap-2"
            style={{ alignItems: "flex-start", marginTop: index === 0 ? "1rem" : "" }}
          >
            <div className="p-inputgroup flex-1">
              <span className="p-inputgroup-addon">@</span>
              <FloatLabel>
                <InputText
                  id={`email-${index}`}
                  name="email"
                  data-testid={`email-input-${index}`}
                  value={input.email}
                  className={emailErrors[index] ? "p-invalid" : ""}
                  onChange={e =>
                    setInputValues(prev =>
                      prev.map((item, i) =>
                        i === index ? { ...item, email: e.target.value } : item
                      )
                    )
                  }
                  autoComplete="off"
                  aria-label="Email"
                  aria-invalid={!!emailErrors[index]}
                  aria-describedby={emailErrors[index] ? `email-error-${index}` : undefined}
                />
                <label htmlFor={`email-${index}`}>Email</label>
              </FloatLabel>
            </div>
            {emailErrors[index] && (
              <small id={`email-error-${index}`} role="alert" className="p-error self-center">
                {emailErrors[index]}
              </small>
            )}
            <div className="p-inputgroup flex-1">
              <span className="p-inputgroup-addon">
                <i className="pi pi-user" aria-hidden="true" />
              </span>
              <FloatLabel>
                <InputText
                  id={`firstName-${index}`}
                  data-testid={`firstName-input-${index}`}
                  value={input.firstName}
                  onChange={e =>
                    setInputValues(prev =>
                      prev.map((item, i) =>
                        i === index ? { ...item, firstName: e.target.value } : item
                      )
                    )
                  }
                  autoComplete="off"
                  aria-label="First Name"
                />
                <label htmlFor={`firstName-${index}`}>First Name</label>
              </FloatLabel>
            </div>
            <div className="p-inputgroup flex-1">
              <span className="p-inputgroup-addon">
                <i className="pi pi-user" aria-hidden="true" />
              </span>
              <FloatLabel>
                <InputText
                  id={`lastName-${index}`}
                  data-testid={`lastName-input-${index}`}
                  value={input.lastName}
                  onChange={e =>
                    setInputValues(prev =>
                      prev.map((item, i) =>
                        i === index ? { ...item, lastName: e.target.value } : item
                      )
                    )
                  }
                  autoComplete="off"
                  aria-label="Last Name"
                />
                <label htmlFor={`lastName-${index}`}>Last Name</label>
              </FloatLabel>
            </div>
            <div className="p-inputgroup flex-1">
              <span className="p-inputgroup-addon">
                <i className="pi pi-tags" aria-hidden="true" />
              </span>
              <FloatLabel>
                <Dropdown
                  inputId={`role-${index}`}
                  data-testid={`role-dropdown-${index}`}
                  value={input.role}
                  options={[
                    { label: "Learner", value: "learner" },
                    { label: "Admin", value: "admin" },
                  ]}
                  onChange={e =>
                    setInputValues(prev =>
                      prev.map((item, i) =>
                        i === index ? { ...item, role: e.value as "learner" | "admin" } : item
                      )
                    )
                  }
                  className="w-full"
                  checkmark
                  highlightOnSelect={false}
                />
                <label htmlFor={`role-${index}`}>Role</label>
              </FloatLabel>
            </div>
            {index > 0 && (
              <Button
                icon="pi pi-trash"
                severity="danger"
                text
                type="button"
                aria-label="Remove row"
                onClick={() => setInputValues(prev => prev.filter((_, i) => i !== index))}
              />
            )}
            {inputErrorIndex.includes(index) && (
              <i className="pi pi-times-circle text-red-500 self-center" aria-hidden="true" />
            )}
            {inputSuccessIndex.includes(index) && (
              <i className="pi pi-check-circle text-green-500 self-center" aria-hidden="true" />
            )}
          </div>
        ))}
        <Button
          type="button"
          icon="pi pi-plus"
          label="Add another"
          text
          onClick={() => setInputValues(prev => [...prev, { ...EMPTY_INPUT_ROW }])}
        />
      </div>
    </Dialog>
  );
}
